import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth, useCan } from "../auth/AuthProvider";
import { Icon } from "../components/icons";
import {
  Alert,
  Card,
  EmptyState,
  LoadingBlock,
  Mono,
  PageHeader,
  Table,
  Td,
  Th,
  buttonStyles,
  cx,
} from "../components/ui";
import { nextRun } from "../lib/cron";
import { relativeTime } from "../lib/format";
import { useSchedules } from "../lib/queries";
import type { Schedule } from "../lib/types";

/** Cents to a plain dollar figure. Every job is the same flat price. */
const monthly = (cents: number) => `$${(cents / 100).toFixed(2)}`;

type Filter = "all" | "active" | "paused" | "broken";

/* The rungs in the order they are shown. Their words are in `schedules.json`. */
const FILTERS: Filter[] = ["all", "active", "paused", "broken"];

function matches(filter: Filter, job: Schedule): boolean {
  if (filter === "all") return true;
  if (filter === "broken") return !job.provisioned;
  if (filter === "paused") return job.provisioned && !job.enabled;
  return job.provisioned && job.enabled;
}

/**
 * When a job next fires, in its own timezone, as a short phrase.
 *
 * Only for jobs that will actually fire. A paused one has no next run and a
 * countdown beside it would be a promise the platform is not keeping; an
 * unprovisioned one has no cron at all.
 */
function nextRunLabel(job: Schedule): string | null {
  if (!job.enabled || !job.provisioned) return null;
  const at = nextRun(job.cronExpression, job.timezone);
  return at ? relativeTime(at.toISOString()) : null;
}

/**
 * Every scheduled job the organization owns, grouped by the app it runs in.
 *
 * Org-wide rather than a tab under each app, because "my scheduled jobs" is how
 * somebody holds them — a nightly prune and a weekly report are one list even
 * when they live in different apps. The per-app view is this same query with a
 * filter.
 */
export function SchedulesPage() {
  const { t } = useTranslation("schedules");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = useCan("developer");

  const schedules = useSchedules(orgId);
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  const [filter, setFilter] = useState<Filter>("all");

  const rows = useMemo(() => schedules.data?.data ?? [], [schedules.data]);
  const monthlyTotal = schedules.data?.meta.monthlyCentsTotal ?? 0;

  const counts = useMemo(() => {
    const tally: Record<Filter, number> = { all: rows.length, active: 0, paused: 0, broken: 0 };
    for (const job of rows) {
      if (!job.provisioned) tally.broken += 1;
      else if (!job.enabled) tally.paused += 1;
      else tally.active += 1;
    }
    return tally;
  }, [rows]);

  const visible = useMemo(() => rows.filter((job) => matches(filter, job)), [rows, filter]);

  // Grouped in render rather than by a second query: the list is bounded at 50
  // and the API already orders by app then name, so the grouping is a fold.
  const byApp = useMemo(() => {
    const groups = new Map<string, Schedule[]>();
    for (const job of visible) {
      const existing = groups.get(job.appId);
      if (existing) existing.push(job);
      else groups.set(job.appId, [job]);
    }
    return [...groups.values()];
  }, [visible]);

  const hasJobs = rows.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={
          currentOrg
            ? t("description", { organization: currentOrg.name })
            : t("descriptionNoOrg")
        }
        action={
          canManage ? (
            <Link to="/schedules/new" className={buttonStyles("primary")}>
              <Icon name="plus" className="size-3.5" />
              {t("create")}
            </Link>
          ) : undefined
        }
      />

      {notice && <Alert tone="warning">{notice}</Alert>}
      {schedules.error && <Alert>{schedules.error.message}</Alert>}

      {/*
        An operational snapshot, not furniture: each cell filters to the state
        it counts. "Not created" earns its place by being the one state where a
        job silently does nothing, and "Paused" by being the one where a job
        does nothing and is still charged.
      */}
      {hasJobs && (
        <div className="grid grid-cols-2 gap-px border border-line bg-line sm:grid-cols-4">
          {FILTERS.map((key) => {
            const count = counts[key];
            const isActive = filter === key;
            const isBad = key === "broken" && count > 0;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                aria-pressed={isActive}
                className={cx(
                  "bg-canvas px-4 py-3.5 text-left transition-colors duration-200 ease-smooth hover:bg-raised",
                  isActive ? "bg-raised" : "text-fg-muted",
                  isBad && "text-danger",
                )}
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-medium">{t(`filters.${key}.label`)}</span>
                  <span
                    className={cx(
                      "text-lg font-medium tracking-tight tabular-nums",
                      isBad ? "text-danger" : "text-fg",
                    )}
                  >
                    {count}
                  </span>
                </span>
                <span className="mt-1 block text-[11px] text-fg-subtle">
                  {t(`filters.${key}.summary`)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {schedules.isPending ? (
        <Card>
          <LoadingBlock />
        </Card>
      ) : !hasJobs ? (
        <Card>
          <EmptyState
            icon="clock"
            title={t("empty.title")}
            description={t("empty.description")}
            action={
              canManage ? (
                <Link to="/schedules/new" className={buttonStyles("primary")}>
                  {t("empty.create")}
                </Link>
              ) : undefined
            }
          />
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState
            icon="clock"
            title={t("noMatch.title", { filter: t(`filters.${filter}.label`).toLocaleLowerCase() })}
            description={t("noMatch.description")}
            action={
              <button
                type="button"
                onClick={() => setFilter("all")}
                className={buttonStyles("secondary", "sm")}
              >
                {t("noMatch.showAll")}
              </button>
            }
          />
        </Card>
      ) : (
        <Card>
          {/*
            One table for every app, not a table per card.
            Two stacked tables size their columns independently, so "Schedule"
            started at a different x in each one and the eye could not run down
            a column. A group row inside a single table keeps the grid true and
            still says which app each block belongs to.
          */}
          <Table
            head={
              <>
                <Th>{t("columns.job")}</Th>
                <Th>{t("columns.schedule")}</Th>
                <Th priority="sm">{t("columns.nextRun")}</Th>
                <Th priority="lg">{t("columns.command")}</Th>
                <Th priority="md">{t("columns.lastManualRun")}</Th>
                <Th className="text-right">{t("columns.cost")}</Th>
              </>
            }
          >
            {byApp.flatMap((jobs) => {
              const first = jobs[0];
              if (!first) return [];
              return [
                <tr key={`app-${first.appId}`} className="bg-raised/40">
                  <td colSpan={6} className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                      <div className="flex items-center gap-2">
                        <Icon name="apps" className="size-3.5 text-fg-subtle" />
                        <Link
                          to={`/apps/${first.appId}/schedules`}
                          className="text-[13px] font-medium text-fg hover:underline"
                        >
                          {first.appName}
                        </Link>
                        {/* Worth saying beside the app rather than per row: its
                            jobs are still charged, and whether they still fire
                            is the first question somebody has. */}
                        {first.appState === "stopped" && (
                          <span className="rounded-md border border-warn/30 bg-warn/8 px-1.5 py-0.5 text-[11px] text-warn">
                            {t("appStopped")}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-fg-subtle">
                        {t("jobCount", { count: jobs.length })}
                      </span>
                    </div>
                  </td>
                </tr>,
                ...jobs.map((job) => {
                  const upcoming = nextRunLabel(job);
                  return (
                    <tr key={job.id} className="transition-colors hover:bg-raised/50">
                      <Td>
                        <Link
                          to={`/schedules/${job.id}`}
                          className="font-medium text-fg hover:underline"
                        >
                          {job.name}
                        </Link>
                        {(!job.enabled || !job.provisioned) && (
                          <div className="mt-1">
                            {/*
                              An unprovisioned job never runs and is never
                              charged; a paused one never runs and is. Somebody
                              reading a list of things they pay for is owed both
                              distinctions, in the row rather than a tooltip.
                            */}
                            {!job.provisioned ? (
                              <span className="rounded-md border border-danger/30 bg-danger/8 px-1.5 py-0.5 text-[11px] text-danger">
                                {t("notCreated")}
                              </span>
                            ) : (
                              <span className="rounded-md border border-line bg-raised px-1.5 py-0.5 text-[11px] text-fg-subtle">
                                {t("paused")}
                              </span>
                            )}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div className="text-fg">{job.cronDescription}</div>
                        <div className="mt-0.5 text-[11px] text-fg-subtle">{job.timezone}</div>
                      </Td>
                      <Td priority="sm" className="text-fg-muted">
                        {upcoming ?? <span className="text-fg-subtle">—</span>}
                      </Td>
                      <Td priority="lg">
                        <Mono className="text-[12px] text-fg-muted">
                          {job.command.length > 44 ? `${job.command.slice(0, 44)}…` : job.command}
                        </Mono>
                      </Td>
                      <Td priority="md" className="text-fg-muted">
                        {job.lastManualRunAt ? relativeTime(job.lastManualRunAt) : "—"}
                      </Td>
                      <Td className="text-right tabular-nums text-fg-muted">
                        {job.provisioned ? (
                          t("perMonth", { amount: monthly(job.price.monthlyCents) })
                        ) : (
                          <span className="text-fg-subtle">{t("notBilled")}</span>
                        )}
                      </Td>
                    </tr>
                  );
                }),
              ];
            })}
          </Table>

          {/*
            The total belongs where the jobs are, not only on the billing page.
            Paused jobs are inside this figure, which is the whole reason to show
            it: turning one off does not turn off its charge.
          */}
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-line px-4 py-3 text-[13px]">
            <p className="text-fg-muted">
              {t("total", { count: counts.all })}
              {counts.paused > 0 && t("totalPaused", { count: counts.paused })}
            </p>
            <p className="text-fg-muted">
              <Trans
                ns="schedules"
                i18nKey="monthly"
                values={{ amount: monthly(monthlyTotal) }}
                components={{ amount: <span className="tabular-nums text-fg" /> }}
              />
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
